/**
 * In-memory buffer of recent user actions
 * Used to provide context when errors occur
 *
 * PRD-ADM-JOURNEY-001 Task 1.3
 *
 * Provides:
 * - recordUserAction() - adds action to buffer (max 50)
 * - getRecentUserActions() - retrieves last N actions
 * - clearActionBuffer() - clears buffer
 *
 * @canon CANON.md section 4 - RPC-only architecture
 */

interface UserAction {
  ts: number;
  event: string;
  payload: Record<string, unknown>;
}

const MAX_BUFFER_SIZE = 50;
const actionBuffer: UserAction[] = [];

/**
 * Records a user action in the in-memory buffer
 *
 * Automatically maintains max buffer size by removing oldest entries.
 * Used to provide context for error debugging.
 *
 * @param event - Event name (e.g., 'ux.button_click', 'ux.page_view')
 * @param payload - Event payload with additional context
 */
export function recordUserAction(event: string, payload: Record<string, unknown>): void {
  actionBuffer.push({
    ts: Date.now(),
    event,
    payload,
  });

  // Maintain max buffer size (FIFO)
  if (actionBuffer.length > MAX_BUFFER_SIZE) {
    actionBuffer.shift();
  }
}

/**
 * Retrieves recent user actions from buffer
 *
 * Returns actions in chronological order (oldest first).
 * Used when error occurs to provide debugging context.
 *
 * @param count - Number of recent actions to retrieve (default: 10)
 * @returns Array of recent user actions
 */
export function getRecentUserActions(count: number = 10): UserAction[] {
  return actionBuffer.slice(-count);
}

/**
 * Clears the action buffer
 *
 * Should be called on logout or session end to prevent
 * data leakage between users.
 */
export function clearActionBuffer(): void {
  actionBuffer.length = 0;
}
